import {objectFromEntries} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {Section} from 'constants/NavConstants';
import {ReplaceWarningInfoKey} from 'molbio/biointelligent-links/replace-link/constants';
import PaneWithSidebar from 'molbio/biointelligent-links/replace-link/PaneWithSidebar';
import {BlockingEditReason, type ContainerEditsResult} from 'molbio/dao/SequenceDAO';
import ItemDisplayTable from 'nav/expanded/ItemDisplayTable';
import {
  FONT_PROXIMA_14,
  FONT_PROXIMA_13,
  FONT_PROXIMA_11,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as nyc from 'ui-library/NYC';

const WARNING_KEY_TO_METADATA: {
  [warningKey in ReplaceWarningInfoKey]: {color: string; explanationCopy: string};
} = {
  [ReplaceWarningInfoKey.OVERLAPPING_LINKS]: {
    color: Color.RED_400,
    explanationCopy: 'Loss of overlapping part, translation link, or transcription link',
  },
  [ReplaceWarningInfoKey.REMOVED_FEATURES]: {
    color: Color.ORANGE_400,
    explanationCopy: 'Loss of overlapping annotations',
  },
  [ReplaceWarningInfoKey.MUTATED_FEATURES]: {
    color: Color.PURPLE_400,
    explanationCopy: 'Naturally resolved change to annotation or translation',
  },
};

const ORDERED_WARNING_KEYS = [
  ReplaceWarningInfoKey.OVERLAPPING_LINKS,
  ReplaceWarningInfoKey.REMOVED_FEATURES,
  ReplaceWarningInfoKey.MUTATED_FEATURES,
];

const INFO_ICON_SIZE = 8;

const BLOCKED_REASON_TO_LABEL: Record<BlockingEditReason, string> = {
  [BlockingEditReason.INSUFFICIENT_PERMISSIONS]: 'Insufficient permissions',
  [BlockingEditReason.CONTAINED_IN_COMPLEX_POLYMER]: 'Contained in complex polymer',
  [BlockingEditReason.NESTED_LINKS]: 'Nested links',
  [BlockingEditReason.SELF_OVERLAP]: 'Self-overlapping links',
  [BlockingEditReason.MODIFIED_NUCLEOTIDES_AFFECTED]: 'Modified nucleotides affected',
};

interface InfoIconProps {
  warningKey: ReplaceWarningInfoKey;
}

function InfoIcon(props: InfoIconProps): JSX.Element {
  return (
    <svg width={INFO_ICON_SIZE} height={INFO_ICON_SIZE} className={css(styles.legendIcon)}>
      <circle
        cx={INFO_ICON_SIZE / 2}
        cy={INFO_ICON_SIZE / 2}
        r={INFO_ICON_SIZE / 2}
        fill={WARNING_KEY_TO_METADATA[props.warningKey].color}
      />
    </svg>
  );
}

function StatusCell(props: {result: ContainerEditsResult | undefined}): JSX.Element {
  const {result} = props;
  if (result == null) {
    return <span className={css(styles.statusMuted)}>—</span>;
  }
  if (result.type === 'blocked') {
    return <span className={css(styles.blockedReason)}>{BLOCKED_REASON_TO_LABEL[result.reason]}</span>;
  }
  const counts: Array<{key: ReplaceWarningInfoKey; n: number}> = [
    {key: ReplaceWarningInfoKey.OVERLAPPING_LINKS, n: result.overlappingLinkRemovalCount},
    {key: ReplaceWarningInfoKey.REMOVED_FEATURES, n: result.featureRemovalCount},
    {key: ReplaceWarningInfoKey.MUTATED_FEATURES, n: result.featureMutationCount},
  ];
  const nonZero = counts.filter((c) => c.n > 0);
  if (nonZero.length === 0) {
    return <span className={css(styles.statusMuted)}>No issues</span>;
  }
  return (
    <div className={css(styles.statusDots)} role="status">
      {nonZero.map(({key, n}) => (
        <span
          key={key}
          className={css(styles.statusDotGroup)}
          title={WARNING_KEY_TO_METADATA[key].explanationCopy}
        >
          <InfoIcon warningKey={key} />
          <span className={css(styles.statusCount)}>{n}</span>
        </span>
      ))}
    </div>
  );
}

export interface ConfirmPropagationPaneProps {
  selectedIds: Array<string>;
  confirmedIds: Array<string>;
  onToggleConfirmedIds: (ids: Array<string>, isSelected: boolean) => void;
  sidebarExpanded: boolean;
  onToggleSidebarExpanded: () => void;
  containerEditResultsByPolymerId: Record<string, ContainerEditsResult>;
  isLoadingEditEffects: boolean;
}

export default function ConfirmPropagationPane(props: ConfirmPropagationPaneProps): JSX.Element {
  const disabledIds = Object.entries(props.containerEditResultsByPolymerId)
    .filter(([, r]) => r.type === 'blocked')
    .map(([id]) => id);

  const additionalColumnData = [
    {
      columnName: 'Status',
      idToReactNode: objectFromEntries(
        props.selectedIds.map((id) => [
          id,
          <StatusCell key={id} result={props.containerEditResultsByPolymerId[id]} />,
        ])
      ),
      getTargetIndex: () => 1,
    },
  ];

  return (
    <PaneWithSidebar
      sidebarExpanded={props.sidebarExpanded}
      onToggleSidebarExpanded={props.onToggleSidebarExpanded}
      sidebarContent={
        <>
          <p className={css(styles.helpText)}>
            Confirm selected sequences you want to update by replacing the current link with the updated link
          </p>
          <p className={css(styles.helpText)}>
            Replacing the link may result in a loss of overlapping links and/or features. See legend to review
            issues on selected sequences
          </p>

          <div className={css(styles.legendLabel)}>Issue legend</div>
          <div className={css(styles.legend)}>
            {ORDERED_WARNING_KEYS.map((warningKey: ReplaceWarningInfoKey, i: number) => (
              <div
                key={warningKey}
                className={css(
                  styles.legendItem,
                  i !== ORDERED_WARNING_KEYS.length && styles.notLastLegendItem
                )}
              >
                <InfoIcon warningKey={warningKey} />
                <span>{WARNING_KEY_TO_METADATA[warningKey].explanationCopy}</span>
              </div>
            ))}
          </div>
        </>
      }
    >
      <nyc.Container size="lg" className={classNames('expandedContainer', css(styles.tableContainer))}>
        <nyc.Row className="nyc-row--fluid">
          <nyc.Col lg={1} />
          <nyc.Col lg={20} className="u-flex-display u-vertical-flex">
            {props.isLoadingEditEffects ? (
              <CenteredHorizontalThrobber />
            ) : (
              <ItemDisplayTable
                ids={props.selectedIds}
                onToggleSelectedIds={props.onToggleConfirmedIds}
                isCompact={true}
                searchSection={Section.MOLBIO_BIOINTELLIGENT_LINK_COMPONENT_REPLACE}
                selectedIds={props.confirmedIds}
                disabledIds={disabledIds}
                additionalColumnData={additionalColumnData}
              />
            )}
          </nyc.Col>
          <nyc.Col lg={1} />
        </nyc.Row>
      </nyc.Container>
    </PaneWithSidebar>
  );
}

const styles = StyleSheet.create({
  helpText: {
    ...FONT_PROXIMA_14,
    marginBottom: '24px',
  },
  tableContainer: {
    height: '100%',
  },
  legendLabel: {
    ...FONT_PROXIMA_11,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    textTransform: 'uppercase',
    color: Color.GRAY_600,
    marginBottom: '8px',
  },
  legend: {
    ...FONT_PROXIMA_13,
    borderRadius: '8px',
    padding: '8px 12px',
    border: `1px solid ${Color.GRAY_200}`,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
  },
  notLastLegendItem: {
    marginBottom: '4px',
  },
  legendIcon: {
    flexShrink: 0,
  },
  statusDots: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '8px',
  },
  statusDotGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  statusCount: {
    ...FONT_PROXIMA_13,
  },
  statusMuted: {
    ...FONT_PROXIMA_13,
    color: Color.GRAY_500,
  },
  blockedReason: {
    ...FONT_PROXIMA_13,
    color: Color.ORANGE_600,
  },
});
